import { defineStore } from 'pinia';
import { computed, ref } from 'vue-demi';
import { Data, DataTypePlural, Filter } from '@ilteducation/content-ref-data-types';
import { getRefDataByIds, getRefDatas, Response } from './api';

type ContentRefDataState = {
  refDataType: DataTypePlural;
  isLoading: boolean;
  contents: Data[];
};

type RefDataByType = Record<DataTypePlural, ContentRefDataState | undefined>;

const emptyState: RefDataByType = {
  categories: undefined,
  languages: undefined,
  publishers: undefined,
  series: undefined,
  areas: undefined,
  keywords: undefined,
};

const useContentRefData = defineStore('content-ref-data', () => {
  const state = ref<RefDataByType>(emptyState);

  const isLoading = computed(() => (type: DataTypePlural) => state.value[type]?.isLoading ?? false);
  const all = computed(
    () =>
      (type: DataTypePlural): Data[] =>
        (state.value[type]?.contents ?? []) as Data[],
  );
  const byId = computed(
    () =>
      (type: DataTypePlural, referenceId: string): Data | undefined =>
        state.value[type]?.contents.find((data) => data.id === referenceId) as Data | undefined,
  );

  const loadReferences = async (refDataType: DataTypePlural, filters?: Filter[], query?: string) => {
    if (isLoading.value(refDataType)) {
      return;
    }

    state.value = {
      ...state.value,
      [refDataType]: {
        refDataType,
        isLoading: false,
        contents: state.value[refDataType]?.contents ?? [],
      },
    };

    let cursor: string | null = null;
    let allData: Data[] = [];
    do {
      const res: Response<Data> = await getRefDatas<Data>(refDataType, cursor, filters, query);
      const refData = res.data as Data[];
      allData = [...allData, ...refData];
      cursor = res.cursor;
    } while (cursor);

    const deduplicatedContents = state.value?.[refDataType]?.contents
      ? Array.from(
          new Map([...(state.value[refDataType]?.contents || []), ...allData].map((item) => [item.id, item])).values(),
        )
      : allData;
    state.value = {
      ...state.value,
      [refDataType]: {
        refDataType,
        isLoading: false,
        contents: deduplicatedContents,
      },
    };
  };

  const loadByIds = async (refDataType: DataTypePlural, ids: string[]): Promise<Data[]> => {
    const notLoadedIds = ids.filter((id) => !byId.value(refDataType, id));
    const loadedKeywords = ids.map((id) => byId.value(refDataType, id)).filter((data): data is Data => !!data);
    if (notLoadedIds.length === 0) {
      return loadedKeywords;
    }

    const newRefData = await getRefDataByIds(notLoadedIds);
    state.value = {
      ...state.value,
      [refDataType]: {
        refDataType,
        isLoading: false,
        contents: [...(state.value[refDataType]?.contents ?? []), ...newRefData],
      },
    };

    return [...loadedKeywords, ...newRefData];
  };

  const clear = () => {
    state.value = emptyState;
  };

  return {
    result: all, // deprecated
    reference: byId, // deprecated
    all,
    byId,
    loadByIds,
    isLoading,
    references: loadReferences,
    clear,
  };
});

export default useContentRefData;
